import { ethers } from 'ethers';
import StakingABI from '../abis/Staking.json';
import { getSigner } from '../lib/web3';
import { addressSchema } from '../models/schema';
import UpdateValidatorInfoRequest from '../models/UpdateValidateInfoRequest';
import { ip2int, int2ip, getAdjustedGas } from '../utils';

const DEFAULT_REALM_ID = 1;

export const requestToLeave = async (stakingContractAddress: string) => {
  // Validation
  try {
    await addressSchema.validate(stakingContractAddress);
  } catch (e: any) {
    throw new Error(`Error validating request: ${e.message}`);
  }

  const signer = await getSigner();

  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );

  const tx = await stakingContract.requestToLeave();

  console.log('requestToLeave tx for wallet: ', tx);
};

export const requestToJoin = async (stakingContractAddress: string) => {
  // Validation
  try {
    await addressSchema.validate(stakingContractAddress);
  } catch (e: any) {
    throw new Error(`Error validating request: ${e.message}`);
  }

  const signer = await getSigner();
  const stakingAddress = await signer.getAddress();

  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );

  const existingInfo = await stakingContract.validators(stakingAddress);
  console.log('existingInfo: ', existingInfo);

  if (
    existingInfo.nodeAddress === '0x0000000000000000000000000000000000000000'
  ) {
    throw new Error(
      'Node address cannot be zeroes. Please check your staker wallet connection.'
    );
  }

  const tx = await stakingContract.requestToJoin(DEFAULT_REALM_ID);

  console.log('requestToJoin tx for wallet: ', tx);
  await tx.wait();
};

export const unstakeAndGetRewards = async (stakingContractAddress: string) => {
  // Validation
  try {
    await addressSchema.validate(stakingContractAddress);
  } catch (e: any) {
    throw new Error(`Error validating request: ${e.message}`);
  }

  const signer = await getSigner();

  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );

  const tx = await stakingContract.exit();
  console.log('exit tx for wallet: ', tx);
};

export const setIpPortNodeAddress = async (
  request: UpdateValidatorInfoRequest,
  stakingContractAddress: string
) => {
  // Validation
  try {
    await request.validate();
    await addressSchema.validate(stakingContractAddress);
  } catch (e: any) {
    throw new Error(
      `Error validating setIpPortNodeAddress request: ${e.message}`
    );
  }

  const signer = await getSigner();

  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );

  const txData =
    await stakingContract.populateTransaction.setIpPortNodeAddress(
      ethers.BigNumber.from(ip2int(request.ip)),
      ethers.BigNumber.from(ip2int(request.ipv6)), // Not needed for now
      ethers.BigNumber.from(request.port),
      request.nodeAddress,
    );

  const _adjustedGas = await getAdjustedGas({ signer, txData });

  const tx = await stakingContract.setIpPortNodeAddress(
    ethers.BigNumber.from(ip2int(request.ip)),
    ethers.BigNumber.from(ip2int(request.ipv6)), // Not needed for now
    ethers.BigNumber.from(request.port),
    request.nodeAddress,
    {
      gasLimit: _adjustedGas,
    }
  );
  console.log(
    'setIpPortNodeAddress tx for wallet: ',
    tx
  );
};

export const getNodeInfo = async (
  stakingContractAddress: string,
  stakingWalletAddress: string
) => {
  // Validation
  try {
    await addressSchema.validate(stakingContractAddress);
    await addressSchema.validate(stakingWalletAddress);
  } catch (e: any) {
    throw new Error(`Error validating request: ${e.message}`);
  }

  const signer = await getSigner();

  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );

  let existingInfo;
  try {
    existingInfo = await stakingContract.validators(stakingWalletAddress);
  } catch (e: any) {
    const cause =
      e?.error?.message ||
      e?.data?.message ||
      e?.reason ||
      e?.message ||
      'Unknown error';
    // Using alert as a stand-in for a toast bar notification in this service layer
    alert(`Error getting validator info: ${cause}`);
    return null;
  }
  console.log('existingInfo', existingInfo);
  // format stuff properly
  const formattedNodeInfo = {
    ip: int2ip(existingInfo.ip),
    ipv6: existingInfo.ipv6.toString(), // Not needed for now
    port: existingInfo.port.toString(),
    nodeAddress: existingInfo.operatorAddress.toString(),
  };

  const inCurrentEpoch = await stakingContract.isActiveValidator(
    DEFAULT_REALM_ID,
    stakingWalletAddress
  );
  const allValidatorsInNextEpoch =
    await stakingContract.getValidatorsInNextEpoch(DEFAULT_REALM_ID);
  const inNextEpoch = allValidatorsInNextEpoch.some(
    (a: string) => a.toLowerCase() === stakingWalletAddress.toLowerCase()
  );

  return {
    nodeInfo: formattedNodeInfo,
    inCurrentEpoch,
    inNextEpoch,
  };
};

export const isActiveValidator = async (
  stakingContractAddress: string,
  stakingWalletAddress: string
) => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);
  await addressSchema.validate(stakingWalletAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const signer = provider.getSigner();
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );
  const inCurrentEpoch = await stakingContract.isActiveValidator(
    DEFAULT_REALM_ID,
    stakingWalletAddress
  );
  console.log('Is active validator in current epoch: ', inCurrentEpoch);
  const inNextEpoch = await stakingContract.validatorsInNextEpoch(
    DEFAULT_REALM_ID,
    stakingWalletAddress
  );
  console.log('Is active validator in next epoch: ', inNextEpoch);
  // const validatorsInNextEpoch = await stakingContract.getValidatorsInNextEpoch();
  // console.log("Validators in next epoch: ", validatorsInNextEpoch);
  return inCurrentEpoch || inNextEpoch;
};

export const getNextEpoch = async (
  stakingContractAddress: string
): Promise<any> => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const signer = provider.getSigner();
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );
  const nextEpoch = await stakingContract.nextEpoch();
  console.log('Next epoch: ', nextEpoch);
  return nextEpoch;
};

export const getRewardInfo = async (
  stakingContractAddress: string,
  stakerAddress: string
): Promise<any> => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);
  await addressSchema.validate(stakerAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const signer = provider.getSigner();
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    signer
  );
  const rewardInfo = await stakingContract.getRewardInfo(stakerAddress);
  console.log('Reward info: ', rewardInfo);
  return rewardInfo;
};

export const checkValidatorEpochStatus = async (
  stakingContractAddress: string,
  stakerAddress: string
): Promise<{ inCurrentEpoch: boolean; inNextEpoch: boolean }> => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);
  await addressSchema.validate(stakerAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    provider
  );

  const inCurrentEpoch = await stakingContract.isActiveValidator(
    DEFAULT_REALM_ID,
    stakerAddress
  );

  const allValidatorsInNextEpoch =
    await stakingContract.getValidatorsInNextEpoch(DEFAULT_REALM_ID);
  const inNextEpoch = allValidatorsInNextEpoch.some(
    (a: string) => a.toLowerCase() === stakerAddress.toLowerCase()
  );

  return { inCurrentEpoch, inNextEpoch };
};

export const getMinimumSelfStake = async (
  stakingContractAddress: string
): Promise<ethers.BigNumber> => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    provider
  );

  const minSelfStake = await stakingContract.getMinimumSelfStake();
  return minSelfStake;
};

export const getSelfStake = async (
  stakingContractAddress: string,
  stakerAddress: string
): Promise<ethers.BigNumber> => {
  // Validate address
  await addressSchema.validate(stakingContractAddress);
  await addressSchema.validate(stakerAddress);

  // Connect to the staking contract
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const stakingContract = new ethers.Contract(
    stakingContractAddress,
    StakingABI,
    provider
  );

  try {
    // Mimic lit-stake behavior: use getStakeRecordsForUser(validatorAddress, validatorAddress)
    // to get all self-stake records at once, then sum the amounts
    const stakeRecords = await stakingContract.getStakeRecordsForUser(
      stakerAddress,
      stakerAddress
    );

    let totalSelfStake = ethers.BigNumber.from(0);

    // Sum up all stake record amounts (mimicking lit-stake's totalSelfStakeAmount calculation)
    for (const stakeRecord of stakeRecords) {
      if (stakeRecord && stakeRecord.amount) {
        totalSelfStake = totalSelfStake.add(stakeRecord.amount);
      }
    }

    return totalSelfStake;
  } catch (e: any) {
    console.error('Error getting self-stake:', e);
    // Return 0 if there's any error
    return ethers.BigNumber.from(0);
  }
};
